import { useState, useRef, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import RenderMultiSelect from "./MultiSelect";
import RenderAutoSelect from "./AutoSelect";
import RenderRadioOptions from "./RadioOptions";
import RenderSingleSelect from "./SingleSelect";
import RenderTextInput from "./TextInput";
import { useClickOutside } from "../../utils";
import Filter from "../../assets/icons/svg/filter_list.svg?react";
import { CustomSelect } from "..";
import DateRangePicker from "./DateRange";
import RenderTagsInput from "./Tags";
import RenderViewsInput from "./Views";
import { RootState, store } from "../../store";
import {
  Display_Value_Key as dvk,
  handleFilterChangeUtil,
} from "../Create/utils";
import { range } from "@/database";
import { useSearchParams, useLocation } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import {
  updateIsFilter,
  updateIsClearFilter,
  updatePreviousFilters,
} from "@/store/media/media.action";
import { truncateText } from "@/utils/utilFunctions/truncateText";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";
import {
  getFilterStorageKey,
  readStoredFilters,
  writeStoredFilters,
  clearStoredFilters,
} from "@/utils/utilFunctions/filterStorage";

export interface FilterOption {
  logo?: string | undefined;
  baseURL?: string | undefined;
  label: string;
  value: string;
  displayValue?: displayName;
}

export type displayName =
  | string
  | string[]
  | number
  | number[]
  | boolean
  | null
  | undefined
  | { gt?: number; lt?: number };
export type FilterValue =
  | string
  | string[]
  | number
  | number[]
  | boolean
  | null
  | displayName
  | range;
export type FilterActions = "add" | "remove" | "clear";
export interface Filter {
  displayName: string;
  name: string;
  type:
    | "text"
    | "select"
    | "multiselect"
    | "radio"
    | "date"
    | "tags"
    | "views"
    | "autoselect";
  suffix?: string;
  options?: FilterOption[] | undefined;
  value?: FilterValue;
  displayValue?: string | number | number[] | string[];
  baseURL?: string;
  logo?: string;
}

export interface ObjectValue {}

export interface InputComponentProps {
  filter: Filter;
  onChange: (
    key: string,
    value: FilterValue,
    displayValue: displayName
  ) => void;
  options?: FilterOption[] | undefined;
  preSelected: any;
  onApplyStateChange?: (
    isDisabled: boolean,
    onApply: () => void,
    buttonText?: string
  ) => void;
}
export interface AutoSelectInputProps extends InputComponentProps {
  onInputChangeCallback?: (params: { name: string; keyword: string }) => void;
  updatedOptions?: Record<string, FilterOption[]>;
  loader?: boolean;
}

export interface IFilterProps {
  appliedFilters: Record<string, FilterValue>;
  availableFilters: Filter[];
  handleSetFilters?: any;
  isClearFilter?: boolean;
  filters?: any;
  getListMedia?: any;
  isSetinParams?: boolean;
  apiCallDelayTimer?: number;
  hiddenFilters?: any;
  setHiddenFilters?: any;
  emptyHiddenFilter?: any;
  stopIfNoFilter?: boolean;
  minHiddenFilters?: number;
  showSortBy?: boolean;
  minRowsPerPage?: number;
  onAutoSelectInputChange?: (params: { name: string; keyword: string }) => void;
  autoSelectOptionsMap?: Record<string, FilterOption[]>;
  autoSelectLoader?: boolean;
  onAutoSelectClose?: () => void;
}

const Filters = ({
  appliedFilters,
  availableFilters,
  handleSetFilters,
  isClearFilter,
  updateIsClearFilter,
  filters,
  updateIsFilter,
  getListMedia,
  isSetinParams = false,
  apiCallDelayTimer = 2000,
  hiddenFilters = null,
  setHiddenFilters,
  emptyHiddenFilter,
  stopIfNoFilter = false,
  minHiddenFilters = 0,
  showSortBy = false,
  previousFilters,
  updatePreviousFilters,
  minRowsPerPage = 50,
  onAutoSelectInputChange,
  autoSelectOptionsMap,
  autoSelectLoader = false,
  onAutoSelectClose,
}: Props) => {
  const [selectedFilter, setSelectedFilter] = useState<Filter | null>(null);
  const [showSearchHelper, setShowSearchHelper] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [selectedSearchTag, setSelectedSearchTag] = useState<string>("");
  const [preSelected, setPreSelected] = useState({});
  const selectedFilterRef = useRef<HTMLDivElement>(null);
  const selectedFilterContainerRef = useRef<HTMLDivElement>(null);
  const parentContainerRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef(filters);
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);

  const storageKey = getFilterStorageKey(location.pathname, location.search);
  const seededKeyRef = useRef<string | null>(null);

  const [applyState, setApplyState] = useState({
    isDisabled: true,
    onApply: () => {},
    buttonText: "Apply",
  });

  // const latestFiltersRef = useRef<any>(filters);
  const latestHiddenFiltersRef = useRef<any>(hiddenFilters);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // const isFetchingRef = useRef(false);
  const hasInitialFiltersBeenSetRef = useRef(false);
  const isCurrentlyMakingCallRef = useRef(false);
  const lastTabSwitchRef = useRef<number>(0);

  // Update latestHiddenFiltersRef when hiddenFilters changes
  useEffect(() => {
    latestHiddenFiltersRef.current = hiddenFilters;
  }, [hiddenFilters]);

  const persistFilters = (filters: Record<string, FilterValue>) => {
    if (seededKeyRef.current !== storageKey) return;

    writeStoredFilters(filters, storageKey);
    setSearchParams(
      (prev) => {
        const tab = prev.get("tab");
        return tab ? { tab } : {};
      },
      { replace: true }
    );
  };

  const handleSelectedFilterReset = (key: string) => {
    setToggle(true);
    setSelectedSearchTag(key);
  };
  const handleCrossSelectedFilter = () => {
    setSelectedFilter(null);
    setToggle(false);
    onAutoSelectClose && onAutoSelectClose();
  };
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const handleSelectedFilterOutsideClick = (event: any) => {
    // event.stopPropagation();
    const latestFilters = filtersRef.current || {};
    const hasIdKey = Object.prototype.hasOwnProperty.call(latestFilters, "id");

    setShowSearchHelper(false);
    setSelectedFilter(null);
    setToggle(false);

    if (Object.keys(latestFilters).length === 0 || !hasIdKey) {
      onAutoSelectClose && onAutoSelectClose();
    }
  };
  const handleShowSearchHelper = () => {
    setShowSearchHelper(false);
  };
  const searchHelperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useClickOutside(searchHelperRef, handleShowSearchHelper);
  useClickOutside(searchInputRef, handleShowSearchHelper);
  useClickOutside(selectedFilterContainerRef, () => {
    setSelectedFilter(null);
    setToggle(false);
  });

  const handleApplyFilter = (
    key: string,
    value: FilterValue,
    displayValue: displayName
  ) => {
    // Analytics: Advanced filters
    // Track only for actual filter keys (skip clear/sort/page).
    const analyticsLocation = window.location.pathname.startsWith("/discover")
      ? "discover"
      : "content";
    const filterMeta = availableFilters.find((f) => f.name === key);
    if (
      key &&
      key !== "clear" &&
      key !== "page" &&
      key !== "sort" &&
      key !== "sortBy"
    ) {
      trackEvent("browse", {
        category: "filters",
        entity: "AdvancedFilter",
        action: filterMeta?.displayName || key,
        location: analyticsLocation,
        ...getTrackUserContext(),
      });
    }

    const updatefilters = handleFilterChangeUtil(
      key,
      value,
      displayValue,
      filters,
      updateIsFilter,
      updateIsClearFilter,
      dvk
    );
    // updateSearchParams(updatefilters);
    handleSetFilters(updatefilters);
    // onFilterChange(key, value, displayValue);
    setSelectedFilter(null);
    setToggle(false);
  };

  const handleRemoveSearchTag = (filter: Filter) => {
    const updatefilters = handleFilterChangeUtil(
      filter.name,
      null,
      null,
      filters,
      updateIsFilter,
      updateIsClearFilter,
      dvk
    );
    // updateSearchParams(updatefilters);
    handleSetFilters(updatefilters);
    onAutoSelectClose && onAutoSelectClose();

    // onFilterChange(filter.name, null);
  };

  const handleClearFilters = () => {
    setSelectedFilter(null);
    const updatefilters = handleFilterChangeUtil(
      "clear",
      null,
      null,
      filters,
      updateIsFilter,
      updateIsClearFilter,
      dvk
    );
    // updateSearchParams(updatefilters);

    handleSetFilters(updatefilters);
    // onFilterChange('clear')
    onAutoSelectClose && onAutoSelectClose();
  };

  const handleFilterSelection = (filter: Filter) => {
    // Reset apply state when selecting a new filter
    setApplyState({
      isDisabled: true,
      onApply: () => {},
      buttonText: "Apply",
    });
    setSelectedFilter({ ...filter });
  };

  // Memoized callback for apply state changes
  const handleApplyStateChange = useCallback(
    (
      isDisabled: boolean,
      onApply: () => void,
      buttonText: string = "Apply"
    ) => {
      setApplyState({ isDisabled, onApply, buttonText });
    },
    []
  );

  // Memoized function to create filter components
  const createFilterComponent = useCallback(
    (Component: any, props: any) => {
      return (
        <Component {...props} onApplyStateChange={handleApplyStateChange} />
      );
    },
    [handleApplyStateChange]
  );

  // Common Apply Button Component
  const ApplyButton = ({
    onApply,
    isDisabled = false,
    buttonText = "Apply",
  }: {
    onApply: () => void;
    isDisabled?: boolean;
    buttonText?: string;
  }) => (
    <div className="d-flex justify-content-end" style={{ marginRight: "15px" }}>
      <button
        className="btn btn-primary btn-sm text-white"
        disabled={isDisabled}
        onClick={onApply}
      >
        {buttonText}
      </button>
    </div>
  );

  const handleEnterOnSearchInput = (e: React.KeyboardEvent) => {
    if (!searchInputRef.current) return;
    const value = searchInputRef.current.value || "";
    if (e.key === "Enter" && value) {
      handleApplyFilter(availableFilters[1].name, value, value);
      searchInputRef.current.value = "";
      searchInputRef.current.blur();
      setShowSearchHelper(false);
    }
  };

  const renderSearchTags = () =>
    Object.keys(appliedFilters).map((key) => {
      const filter = availableFilters.find((f) => f.name === key);
      if (!filter) return null;
      const rawValue = appliedFilters[key + dvk];

      // Convert value to a string for rendering
      const value = Array.isArray(rawValue)
        ? rawValue.join(", ")
        : typeof rawValue === "object"
          ? JSON.stringify(rawValue) // Convert object to a string
          : rawValue;
      return (
        <div className="d-flex gap-2 align-items-center flex-column flex-grow-1">
          <span
            key={key}
            className="d-flex px-3 bg-body-secondary rounded-pill align-items-center gap-1"
            onClick={() => handleSelectedFilterReset(key)}
          >
            {filter.displayName} {filter.suffix} '
            {truncateText(String(value), 50)}'
            <i
              className="bi bi-x fs-4 text-primary"
              onClick={() => handleRemoveSearchTag(filter)}
            />
          </span>
          {filter.name === selectedSearchTag ? (
            <div
              ref={selectedFilterRef}
              className="position-absolute "
              style={{ zIndex: 1000, minWidth: "200px" }}
            >
              <AnimatePresence>
                {toggle
                  ? renderSelectedFilter(filter, appliedFilters)
                  : showSearchHelper}
              </AnimatePresence>
            </div>
          ) : (
            ""
          )}
        </div>
      );
    });

  const renderSelectedFilter = (filter: Filter, preSelected: any) => {
    return (
      <motion.div
        className="shadow rounded bg-white pb-3"
        // style={{ minWidth: '200px', maxWidth: '400px' }}
        style={{ width: "265px" }}
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 10 }}
      >
        <div className="d-flex justify-content-between px-2 py-1 bg-light">
          <div className="ms-2 py-2 fw-medium text-uppercase">
            {filter.displayName}
          </div>
          <i
            className="bi bi-x f-30 text-end"
            onClick={() => {
              handleCrossSelectedFilter();
            }}
          />
        </div>

        {filter.type === "date" && (
          <div className="px-3">
            <div className="mb-3 py-3 border-bottom">
              {createFilterComponent(DateRangePicker, {
                filter,
                onChange: handleApplyFilter,
                preSelected,
              })}
            </div>
          </div>
        )}
        {filter.type === "text" &&
          createFilterComponent(RenderTextInput, {
            filter,
            onChange: handleApplyFilter,
            preSelected,
          })}
        {filter.type === "views" &&
          createFilterComponent(RenderViewsInput, {
            filter,
            onChange: handleApplyFilter,
            preSelected,
          })}
        {filter.type === "tags" &&
          createFilterComponent(RenderTagsInput, {
            filter,
            onChange: handleApplyFilter,
            preSelected,
          })}
        {filter.type === "radio" &&
          createFilterComponent(RenderRadioOptions, {
            filter,
            options: filter.options,
            onChange: handleApplyFilter,
            preSelected,
          })}
        {filter.type === "multiselect" &&
          createFilterComponent(RenderMultiSelect, {
            filter,
            options: filter.options,
            onChange: handleApplyFilter,
            preSelected,
          })}
        {filter.type === "autoselect" &&
          createFilterComponent(RenderAutoSelect, {
            filter,
            options: filter.options,
            onChange: handleApplyFilter,
            preSelected,
            onInputChangeCallback: onAutoSelectInputChange,
            updatedOptions: autoSelectOptionsMap,
            loader: autoSelectLoader,
          })}
        {filter.type === "select" &&
          createFilterComponent(RenderSingleSelect, {
            filter,
            options: filter.options,
            onChange: handleApplyFilter,
            preSelected,
          })}

        {/* Common Apply Button */}
        <ApplyButton
          onApply={applyState.onApply}
          isDisabled={applyState.isDisabled}
          buttonText={applyState.buttonText}
        />
      </motion.div>
    );
  };

  const renderAvailableFilters = () => (
    <motion.ul
      className="searchHelper border py-0"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      {availableFilters
        .filter((filter) => !appliedFilters[filter.name])
        .map((filter) => (
          <li
            key={filter.name}
            className="border-bottom p-2 f-12"
            onClick={() => handleFilterSelection(filter)}
          >
            {filter.displayName}
          </li>
        ))}
    </motion.ul>
  );

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        parentContainerRef.current &&
        !parentContainerRef.current.contains(event.target)
      ) {
        handleSelectedFilterOutsideClick(event);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isSetinParams && isFiltersInitialized) {
      persistFilters(filters);
    }
  }, [filters, hiddenFilters, isFiltersInitialized]);

  useEffect(() => {
    if (!isSetinParams) {
      seededKeyRef.current = storageKey;
      setIsFiltersInitialized(true);
      return;
    }

    if (seededKeyRef.current === storageKey) return;

    const stored = readStoredFilters(storageKey);

    seededKeyRef.current = storageKey;
    handleSetFilters(stored);

    setSearchParams(
      (prev) => {
        const tab = prev.get("tab");
        return tab ? { tab } : {};
      },
      { replace: true }
    );

    setIsFiltersInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, isSetinParams]);

  // Ensure filters are initialized even if no search params
  useEffect(() => {
    if (!isFiltersInitialized) {
      setIsFiltersInitialized(true);
    }
  }, [isFiltersInitialized]);

  useEffect(() => {
    if (isClearFilter == false) return;
    if (isClearFilter == true) {
      // setTimeout(() => {
      const updatefilters = handleFilterChangeUtil(
        "clear",
        null,
        null,
        filters,
        updateIsFilter,
        updateIsClearFilter,
        dvk
      );
      // updatefilters.asset_type = 'video';
      handleSetFilters(updatefilters);
      setHiddenFilters && setHiddenFilters({});
      if (isSetinParams) {
        clearStoredFilters(storageKey);
      }
      // emptyHiddenFilter && emptyHiddenFilter();
      // },apiCallDelayTimer );
    }
    updateIsClearFilter(false);
  }, [isClearFilter]);

  useEffect(() => {
    if (!getListMedia || stopIfNoFilter) {
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      const currentFilters = { ...filters };
      if (currentFilters.rows === undefined) {
        currentFilters.rows = minRowsPerPage;
      }
      if (currentFilters.page === undefined) {
        currentFilters.page = 1;
      }

      const filtersToUse = { ...currentFilters, ...hiddenFilters };
      if (isCurrentlyMakingCallRef.current) {
        return;
      }

      let shouldMakeCall = false;

      if (!hasInitialFiltersBeenSetRef.current) {
        hasInitialFiltersBeenSetRef.current = true;
        shouldMakeCall = true;
      } else if (
        filters &&
        Object.keys(filters).length === 0 &&
        hiddenFilters &&
        Object.keys(hiddenFilters).length > 0
      ) {
        const currentTime = Date.now();
        const timeSinceLastTabSwitch = currentTime - lastTabSwitchRef.current;

        if (timeSinceLastTabSwitch > 1000) {
          lastTabSwitchRef.current = currentTime;
          shouldMakeCall = true;
        }
      } else if (
        typeof filtersToUse === "object" &&
        typeof previousFilters === "object" &&
        Object.keys(previousFilters).length > 0 &&
        JSON.stringify(filtersToUse) !== JSON.stringify(previousFilters)
      ) {
        shouldMakeCall = true;
      }

      if (shouldMakeCall) {
        isCurrentlyMakingCallRef.current = true;
        updatePreviousFilters && updatePreviousFilters(filtersToUse);
        getListMedia(filtersToUse);

        setTimeout(() => {
          isCurrentlyMakingCallRef.current = false;
        }, 500);
        return;
      }
      return;
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [
    getListMedia,
    filters,
    hiddenFilters,
    previousFilters,
    updatePreviousFilters,
    stopIfNoFilter,
  ]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={parentContainerRef}
      className="d-flex px-3 py-2 gap-1 border-bottom z-3 align-items-center filterCont bg-white"
    >
      <div className="flex-grow-1">
        <div className="d-flex align-items-center gap-1">
          <Filter
            style={{ height: "24px", width: "24px" }}
            onClick={() => setShowSearchHelper(true)}
            className="iconSvg"
          />
          <div className="d-flex gap-1" style={{ width: "100%" }}>
            <div className="d-flex gap-1 py-1" style={{ overflow: "auto" }}>
              {renderSearchTags()}
            </div>
            <div
              className="position-relative flex-grow-1"
              style={{ minWidth: "200px" }}
            >
              <input
                ref={searchInputRef}
                type="text"
                className="form-control shadow-none border-0 fs-6 px-0"
                onFocus={() => setShowSearchHelper(true)}
                placeholder="Click here to add filters"
                onKeyDown={handleEnterOnSearchInput}
              />
              <div
                ref={selectedFilterRef}
                className="position-absolute"
                style={{ zIndex: 1000 }}
              >
                <AnimatePresence>
                  {selectedFilter
                    ? renderSelectedFilter(selectedFilter, preSelected)
                    : showSearchHelper && renderAvailableFilters()}
                </AnimatePresence>
              </div>
            </div>
            {Object.keys(appliedFilters).filter(
              (key) => key !== "rows" && key !== "page"
            ).length > minHiddenFilters && (
              <i
                className="bi bi-x f-32 pointer"
                onClick={handleClearFilters}
              />
            )}
          </div>
        </div>
      </div>
      {showSortBy && (
        <div className="d-flex align-items-center gap-2 border-start ps-4 ms-4 H">
          <CustomSelect
            label=""
            value={filters.sortBy ? String(filters.sortBy) : ""}
            options={[
              { label: "Newest First", value: "newest" },
              { label: "Oldest First", value: "oldest" },
              { label: "Recently Uploaded", value: "recent" },
              { label: "A to Z", value: "asc" },
              { label: "Z to A", value: "desc" },
              { label: "Longest First", value: "longest" },
              { label: "Shortest First", value: "shortest" },
            ]}
            handleChange={(newValue: any) => {
              handleSetFilters({ ...filters, sortBy: newValue.value });
            }}
            placeholder={"Sort by"}
            menuWidth="160px"
            isMenuWidthFixed={true}
            customStyling={{ width: "160px" }}
            className="custom-select-compact"
          />
        </div>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  previousFilters: state.media.previousFilters,
});

const mapDispatch = {
  updateIsFilter,
  updateIsClearFilter,
  updatePreviousFilters,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & IFilterProps;
export default connector(Filters);
